import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { applyTransfer, readTransfer, transferDownload } from '../../api/business/endpoints';
import { downloadLinkUrl } from '../../api/client';
import type { DataTransfer, TransferMode, TransferStockMode } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatBytes, formatDateTime, formatNumber } from '../../components/format';
import { hasErrors, isRunning, progressPercent } from '../../components/transfers';
import { useLanguage } from '../../i18n/LanguageContext';
import type { MessageKey } from '../../i18n/sk';

import { TransferItemsTable } from './TransferItemsTable';
import { TransferProblems } from './TransferProblems';
import { TransferStatusChip } from './TransferStatusChip';

const MODES: Array<TransferMode> = ['CREATE_ONLY', 'FILL_EMPTY', 'OVERWRITE'];
const STOCK_MODES: Array<TransferStockMode> = ['SET_LEVELS', 'ADD_RECEIPT', 'IGNORE'];

/** The counts an export's or an import's result shows, in this order, when it has them. */
const COUNTS: Array<string> = [
  'products',
  'variants',
  'batches',
  'taxonomy',
  'media',
  'created',
  'updated',
  'skipped',
  'failed',
  'taxonomyCreated',
  'variantsCreated',
  'batchesCreated',
  'photos',
  'files',
  'movements',
  'quantity',
];

/**
 * One transfer as it goes (R0037): its progress while it runs; an export's download; an import's preview — what it
 * would create, update or skip, the taxonomy it would make, its problems — and the choice of mode and stock handling,
 * confirmed; then its result. Asks again every two seconds while a worker is on it.
 */
export const TransferDetail = ({ companyId, transferId }: { companyId: string; transferId: string }) => {
  const { language, t } = useLanguage();
  const [mode, setMode] = useState<TransferMode>('FILL_EMPTY');
  const [stockMode, setStockMode] = useState<TransferStockMode>('SET_LEVELS');
  const query = useQuery({
    queryKey: ['transfer', companyId, transferId],
    queryFn: () => readTransfer(companyId, transferId),
    refetchInterval: (current) =>
      current.state.data === undefined ||
      isRunning(current.state.data.status) ||
      current.state.data.status === 'AWAITING_UPLOAD'
        ? 2000
        : false,
  });
  const queryClient = useQueryClient();
  const status = query.data?.status;

  // The history below and the products' table show the same transfer: they follow each step this panel sees.
  useEffect(() => {
    if (status !== undefined) {
      void queryClient.invalidateQueries({ queryKey: ['transfers'] });
      void queryClient.invalidateQueries({ queryKey: ['transfer-items'] });
    }
  }, [status, queryClient]);

  const apply = useAppMutation(
    () => applyTransfer(companyId, transferId, { mode, stock: stockMode }),
    ['transfer', 'transfers', 'products', 'licence-usage'],
  );
  const download = useAppMutation(
    () => transferDownload(companyId, transferId),
    [],
    (link) => {
      window.location.href = downloadLinkUrl('business', link.path);
    },
  );
  const transfer: DataTransfer | undefined = query.data;

  if (transfer === undefined) {
    return <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />;
  }

  const percent = progressPercent(transfer);
  const shownCounts = COUNTS.filter((key) => transfer.counts[key] !== undefined);
  const preview = transfer.preview;

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6">{t(`transferDirection.${transfer.direction}` as const)}</Typography>
          <TransferStatusChip status={transfer.status} />
          {transfer.phase !== null && transfer.status === 'APPLYING' ? (
            <Chip size="small" label={t(`transferPhase.${transfer.phase}` as const)} />
          ) : null}
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(transfer.createdAt, language)} · {transfer.requestedByEmail}
            {transfer.sizeBytes !== null ? ` · ${formatBytes(transfer.sizeBytes, language)}` : ''}
          </Typography>
        </Stack>
        <ErrorNotice error={query.error ?? apply.error ?? download.error} />

        {isRunning(transfer.status) || transfer.status === 'AWAITING_UPLOAD' ? (
          <Box sx={{ my: 2 }}>
            <LinearProgress
              variant={percent === null ? 'indeterminate' : 'determinate'}
              value={percent ?? 0}
            />
            <Typography variant="caption" color="text.secondary">
              {t(`transferStatusHint.${transfer.status}` as MessageKey)}
              {percent !== null ? ` — ${transfer.progressDone} / ${transfer.progressTotal}` : ''}
            </Typography>
          </Box>
        ) : null}

        {transfer.direction === 'EXPORT' && transfer.status === 'DONE' ? (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ my: 2 }}>
            <Button
              variant="contained"
              disabled={!transfer.downloadable || download.isPending}
              onClick={() => download.mutate(undefined)}
            >
              {t('transfers.download')}
            </Button>
            {transfer.expiresAt !== null ? (
              <Typography variant="body2" color="text.secondary">
                {t('transfers.availableUntil', { date: formatDateTime(transfer.expiresAt, language) })}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {shownCounts.length > 0 && transfer.status !== 'READY' ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 2 }}>
            {shownCounts.map((key) => (
              <Chip
                key={key}
                variant="outlined"
                label={`${t(`transferCount.${key}` as MessageKey)}: ${formatNumber(transfer.counts[key], language)}`}
              />
            ))}
          </Box>
        ) : null}

        {preview !== null && transfer.direction === 'IMPORT' ? (
          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('transfers.previewTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('transfers.previewFrom', {
                producer: `${preview.producer.name} ${preview.producer.version}`,
                date: formatDateTime(preview.createdAt, language),
                languages: preview.languages.join(', '),
              })}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
              <Chip
                color="success"
                variant="outlined"
                label={t('transfers.previewNew', { count: preview.new })}
              />
              <Chip
                color="info"
                variant="outlined"
                label={t('transfers.previewMatched', { count: preview.matched })}
              />
              <Chip
                color="error"
                variant="outlined"
                label={t('transfers.previewInvalid', { count: preview.invalid })}
              />
              <Chip variant="outlined" label={t('transfers.previewVariants', { count: preview.variants })} />
              <Chip
                variant="outlined"
                label={t('transfers.previewStock', {
                  batches: preview.batches,
                  quantity: formatNumber(preview.quantity, language),
                })}
              />
              <Chip variant="outlined" label={t('transfers.previewMedia', { count: preview.mediaFiles })} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {Object.entries(preview.taxonomy)
                .map(([kind, counts]) =>
                  t('transfers.previewTaxonomy', {
                    kind: t(`taxonomyKind.${kind}` as MessageKey),
                    total: counts.total,
                    new: counts.new,
                  }),
                )
                .join(' · ')}
            </Typography>
          </Box>
        ) : null}

        <TransferProblems problems={transfer.problems} />

        {transfer.status === 'READY' ? (
          <Box sx={{ my: 2, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <FormControl>
              <FormLabel>{t('transfers.modeLabel')}</FormLabel>
              <RadioGroup value={mode} onChange={(event) => setMode(event.target.value as TransferMode)}>
                {MODES.map((one) => (
                  <FormControlLabel
                    key={one}
                    value={one}
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2">{t(`transferMode.${one}` as const)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(`transferMode.hint.${one}` as const)}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <FormControl>
              <FormLabel>{t('transfers.stockLabel')}</FormLabel>
              <RadioGroup
                value={stockMode}
                onChange={(event) => setStockMode(event.target.value as TransferStockMode)}
              >
                {STOCK_MODES.map((one) => (
                  <FormControlLabel
                    key={one}
                    value={one}
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2">{t(`transferStock.${one}` as const)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(`transferStock.hint.${one}` as const)}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <Box>
              <Button
                variant="contained"
                disabled={apply.isPending || hasErrors(transfer.problems)}
                onClick={() => apply.mutate(undefined)}
              >
                {t('transfers.apply')}
              </Button>
            </Box>
          </Box>
        ) : null}

        {transfer.status === 'DONE' && transfer.direction === 'IMPORT' ? (
          <Alert severity="success" sx={{ my: 2 }}>
            {t('transfers.importDone', {
              mode: t(`transferMode.${transfer.mode ?? 'CREATE_ONLY'}` as const),
              stock: t(`transferStock.${transfer.stockMode ?? 'IGNORE'}` as const),
            })}
          </Alert>
        ) : null}

        {transfer.direction === 'IMPORT' && preview !== null ? (
          <Box sx={{ mt: 2 }}>
            <TransferItemsTable
              companyId={companyId}
              transferId={transfer.id}
              applied={transfer.status === 'DONE' || transfer.status === 'APPLYING'}
              mode={transfer.mode ?? mode}
            />
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};
